import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '~/lazy_apollo/client';
import PropTypes from 'prop-types';
import { Grid, Icon, ToggleButton, ToggleButtonGroup, Box, Tooltip } from '@popmenu/common-ui';
import { Typography } from '@material-ui/core';
import { Info } from '@popmenu/web-icons';
import { useIntl } from 'react-intl';
import { useSnackbar } from '~/utils/withSnackbar';

import phoneUsedByMfa from '~/libs/gql/queries/users/phoneUsedByMfaQuery.gql';
import { displayPhoneLastFour, formatPhoneNumberForDisplay } from '../sessions/mfa/phoneUtils';
import { notBlankValidator, phoneValidator } from '../../utils/forms';
import { currentUserShape, withCurrentUser } from '../CurrentSessionProvider';
import { useFeatureFlags } from '../../utils/featureFlagsContext';
import { useStyles } from './styles';
import updateUserMutation from '../../libs/gql/mutations/users/updateUserMutation.gql';

import BasicForm, { CheckBoxGroup, SelectGroup, SubmitGroup, TextFieldGroup } from '../../admin/shared/forms/BasicForm';
import BirthdayFields from './BirthdayFields';

const formatOnboardingRole = onboardingRole => (
  onboardingRole?.replace('role_', '').split('_').map(s => s.charAt(0).toUpperCase() + s.substring(1)).join(' ')
);

const withoutOnboardingRole = (obj) => {
  const { onboardingRole, ...rest } = obj;
  return rest;
};

const withoutPhoneOnly = (obj) => {
  const { phone, ...rest } = obj;
  return rest;
};

export function buildUpdateUserVariables(variables, { userId, isAdminMFAEnabled, isMobile, showPhoneSection }) {
  const baseInput = withoutOnboardingRole(variables);
  const inputForMutation = isAdminMFAEnabled ?
    withoutPhoneOnly(baseInput) :
    baseInput;
  const includeMobileFields = showPhoneSection !== false;
  return {
    userId,
    userInput: {
      ...inputForMutation,
      birthDay: variables.birthDay ? parseInt(variables.birthDay, 10) : null,
      birthMonth: variables.birthMonth ? parseInt(variables.birthMonth, 10) : null,
      ...(includeMobileFields ? {
        isMobilePhone: isMobile,
        optInInformationalTexts: isMobile ? variables.optInInformationalTexts : false,
        optInPromotionalTexts: isMobile ? variables.optInPromotionalTexts : false,
      } : {}),
    },
  };
}

const useMessages = () => {
  const intl = useIntl();
  return {
    numberIsAlreadyUsed: intl.formatMessage({ defaultMessage: 'Number is already linked to another user' }),
    phoneUsedCheckFailed: intl.formatMessage({ defaultMessage: 'Phone used check failed' }),
  };
};

const UserProfileForm = ({ currentUser, isAdmin = false, restaurantId = undefined }) => {
  const { showSnackbarError } = useSnackbar();
  const [phoneValue, setPhoneValue] = useState(currentUser?.phone || '');
  const [checkPhone, { data, loading }] = useLazyQuery(phoneUsedByMfa, { fetchPolicy: 'network-only' });
  const onboardingRole = restaurantId ? currentUser.members.find(member => member.restaurantId === restaurantId)?.userSelectedRole : null;
  const intl = useIntl();
  const messages = useMessages();

  const { isFeatureActive } = useFeatureFlags();
  const showLocaleField = isAdmin && (
    currentUser.adminLocale !== 'en_admin_locale' ||
    currentUser.isRoot ||
    isFeatureActive('admin_locale_user_setting')
  );
  const isAdminMFAEnabled = isFeatureActive('pal9000_admin_mfa');

  const [phoneTypeField, setPhoneTypeField] = useState(currentUser.isMobilePhone ? 'mobile' : 'phone');
  const isMobile = phoneTypeField === 'mobile';
  const handlePhoneTypeChange = (event, value) => {
    event.preventDefault();
    if (value !== null) {
      setPhoneTypeField(value);
    }
  };
  const classes = useStyles();

  const formApiRef = useRef(null);
  // simple cache: remembers the last phone checked and its result
  const lastCheckedRef = useRef({ phone: null, used: null });
  const reqCounter = useRef(0);
  useEffect(() => {
    if (!lastCheckedRef.current.used) return;
    if (lastCheckedRef.current.phone !== phoneValue) {
      lastCheckedRef.current = { phone: phoneValue, used: false };
      formApiRef.current?.setFieldMeta?.('phone', { error: false });
    }
  }, [phoneValue]);
  const combinedValidator = useCallback((value, instance) => {
    if (lastCheckedRef.current && lastCheckedRef.current.used) {
      return messages.numberIsAlreadyUsed;
    }
    const instanceError = instance && instance.meta && instance.meta.error;
    if (instanceError) return instanceError;
    return phoneValidator(value, instance);
  }, [messages.numberIsAlreadyUsed]);
  const handlePhoneBlur = async (e) => {
    const value = (e?.target?.value ?? '').trim();
    if (!value) return;
    // use simple cache to avoid repeated calls for the same value
    if (lastCheckedRef.current.phone === value) {
      return;
    }
    // increment request counter to ignore stale responses later
    reqCounter.current += 1;
    const thisReq = reqCounter.current;
    try {
      const result = await checkPhone({
        variables: { excludeUserId: currentUser?.id || null, phone: value },
      });
      // If an error exists, it throws it.
      if ((result?.errors && result.errors.length > 0) || !result?.data) {
        throw new Error(messages.phoneUsedCheckFailed);
      }
      // if another request was triggered in the meantime, we ignore this response
      if (thisReq !== reqCounter.current) return;
      const used = Boolean(result?.data?.phoneUsedByMfa);
      lastCheckedRef.current = { phone: value, used };
      formApiRef.current?.setFieldMeta('phone', { error: used ? messages.numberIsAlreadyUsed : false });
    } catch (err) {
      showSnackbarError(messages.phoneUsedCheckFailed);
    }
  };
  const handlePhoneChange = useCallback((value) => {
    setPhoneValue(value);
    if (lastCheckedRef.current && lastCheckedRef.current.used && lastCheckedRef.current.phone !== value) {
      lastCheckedRef.current = { phone: value, used: false };
      formApiRef.current?.setFieldMeta?.('phone', { error: false });
    }
  }, []);
  const submitDisabled = !isAdminMFAEnabled && (loading || !!data?.phoneUsedByMfa);
  return (
    <BasicForm
      defaultValues={{
        adminLocale: currentUser.adminLocale,
        birthDay: currentUser.birthDay,
        birthMonth: currentUser.birthMonth,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        onboardingRole: formatOnboardingRole(onboardingRole),
        optInInformationalTexts: currentUser.optInInformationalTexts,
        optInPromotionalTexts: currentUser.optInPromotionalTexts,
        phone: currentUser.phone,
      }}
      labelPosition="top"
      mutate={{
        mutation: updateUserMutation,
        toVariables: variables => buildUpdateUserVariables(variables, {
          isAdminMFAEnabled,
          isMobile,
          showPhoneSection: !isAdminMFAEnabled || !!currentUser?.phone,
          userId: currentUser.id,
        }),
      }}
    >
      {(basicFormApi) => {
        if (!formApiRef.current) formApiRef.current = basicFormApi;
        return (
          <React.Fragment>
            <TextFieldGroup
              disabled={currentUser.isRoot}
              field="email"
              title={intl.formatMessage({ defaultMessage: 'Email' })}
              type="email"
              validate={notBlankValidator}
              variant="standard"
            />
            <TextFieldGroup
              field="firstName"
              title={intl.formatMessage({ defaultMessage: 'First Name' })}
              variant="standard"
            />
            <TextFieldGroup
              field="lastName"
              title={intl.formatMessage({ defaultMessage: 'Last Name' })}
              variant="standard"
            />
            {(!isAdminMFAEnabled || currentUser?.phone) && (
              <React.Fragment>
                <Grid container spacing={2} className={classes.gridPhone}>
                  <Grid item xs={12} md={8} lg={9}>
                    {isAdminMFAEnabled ? (
                      <Box>
                        <Typography component="label" variant="caption" color="textSecondary" display="block">
                          {intl.formatMessage({ defaultMessage: 'Phone' })}
                        </Typography>
                        <Typography className={classes.maskedPhone} variant="body1">
                          {displayPhoneLastFour(formatPhoneNumberForDisplay(currentUser.phone))}
                        </Typography>
                      </Box>
                    ) : (
                      <TextFieldGroup
                        field="phone"
                        title={intl.formatMessage({ defaultMessage: 'Phone' })}
                        type="tel"
                        variant="standard"
                        validate={combinedValidator}
                        onBlur={handlePhoneBlur}
                        onTextChange={handlePhoneChange}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4} lg={3} className={classes.gridToggle}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={phoneTypeField}
                      color="primary"
                      onChange={handlePhoneTypeChange}
                    >
                      <ToggleButton value="phone" data-tour-id="settings-my-profile-phone">
                        {intl.formatMessage({ defaultMessage: 'Phone' })}
                      </ToggleButton>
                      <ToggleButton value="mobile" data-cy="settings-my-profile-mobile" data-tour-id="settings-my-profile-mobile">
                        {intl.formatMessage({ defaultMessage: 'Mobile' })}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
                <Typography
                  variant="subtitle1"
                  className={classes.titleTooltip}
                  style={{ display: isMobile ? 'flex' : 'none' }}
                  data-tour-id="settings-my-profile-opt-in-description"
                >
                  {intl.formatMessage({ defaultMessage: 'Text Notifications' })}
                  <Tooltip title={(
                    <React.Fragment>
                      {intl.formatMessage({ defaultMessage: 'Informational text messaging: By providing your mobile phone number, you are requesting to receive transactional text messages relating to your Popmenu account to the telephone number provided by you. You can opt out at any time by replying with STOP. Message and data rates may apply.' })}
                      <br /><br />
                      {intl.formatMessage({ defaultMessage: 'Promotional text messaging: By checking this box, you are requesting to receive marketing text messages (maximum one per week) from Popmenu and authorizing Popmenu to send text messages to the telephone number provided by you using an automated system for the selection of telephone numbers. You can unsubscribe at any time by replying with STOP. You are not required to agree to enter into this agreement as a condition of purchasing any property, goods, or services. Message and data rates may apply.' })}
                    </React.Fragment>
                  )}
                  >
                    <Box component="span" ml={1} display="inline-flex" alignItems="center">
                      <Icon size="medium" icon={Info} aria-label={intl.formatMessage({ defaultMessage: 'Additional info' })} />
                    </Box>
                  </Tooltip>
                </Typography>
                <Grid container className={classes.gridContainer} style={{ display: isMobile ? 'flex' : 'none' }}>
                  <Grid item xs={6}>
                    <CheckBoxGroup
                      className={classes.checkboxTitle}
                      field="optInInformationalTexts"
                      title={(
                        <span className={classes.checkboxTitle}>
                          {intl.formatMessage({ defaultMessage: 'Opt-in of Informational Text Messaging' })}
                        </span>
                      )}
                      data-tour-id="settings-my-profile-opt-in-informational-texts"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CheckBoxGroup
                      className={classes.checkboxTitle}
                      field="optInPromotionalTexts"
                      title={(
                        <span className={classes.checkboxTitle}>
                          {intl.formatMessage({ defaultMessage: 'Opt-in of Promotional Text Messaging' })}
                        </span>
                      )}
                      data-tour-id="settings-my-profile-opt-in-promotional-texts"
                    />
                  </Grid>
                </Grid>
              </React.Fragment>
            )}
            <BirthdayFields />
            {isAdmin && onboardingRole && (
              <TextFieldGroup
                disabled
                field="onboardingRole"
                title={intl.formatMessage({ defaultMessage: 'Role' })}
                variant="standard"
              />
            )}
            {showLocaleField && (
              <SelectGroup
                field="adminLocale"
                isClearable={false}
                options={[
                  {
                    label: intl.formatMessage({ defaultMessage: 'English' }),
                    value: 'en_admin_locale',
                  },
                  {
                    label: intl.formatMessage({ defaultMessage: 'English (United Kingdom)' }),
                    value: 'en_GB_admin_locale',
                  },
                  {
                    label: intl.formatMessage({ defaultMessage: 'French (Canada)' }),
                    value: 'fr_CA_admin_locale',
                  },
                  {
                    label: intl.formatMessage({ defaultMessage: 'Spanish' }),
                    value: 'es_admin_locale',
                  },
                  {
                    label: intl.formatMessage({ defaultMessage: 'Swedish' }),
                    value: 'sv_admin_locale',
                  },
                ]}
                title={intl.formatMessage({ defaultMessage: 'My Popmenu Language' })}
                variant="standard"
              />
            )}
            <SubmitGroup
              color="primary"
              justifyContent="center"
              title={intl.formatMessage({ defaultMessage: 'Update Profile' })}
              data-tour-id="settings-my-profile-update-profile-button"
              disabled={submitDisabled}
            />
          </React.Fragment>
        );
      }}
    </BasicForm>
  );
};

UserProfileForm.propTypes = {
  currentUser: currentUserShape.isRequired,
  isAdmin: PropTypes.bool,
  restaurantId: PropTypes.number,
};

export default withCurrentUser(UserProfileForm);
